import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Filter, X, Quote } from 'lucide-react';
import { debounce } from 'lodash';
import SearchSuggestions from './SearchSuggestions';
import SearchTips from './SearchTips';
import FilterPopover from './FilterPopover';
import SearchInputWithHighlighting from './SearchInputWithHighlighting';
import useSearchSuggestions from '../../hooks/useSearchSuggestions';
import { useDevMode } from '../../context/DevModeContext';

const DEFAULT_FILTERS = {
  likes: true,
  bookmarks: false,
  user_tweets: false,
  reddit: false,
  roam: true,
  withImages: true,
  withVideos: true,
  textOnly: true,
  favorites: false,
  hideSkipped: true,
};

const FILTER_CHIP_LABELS = {
  likes:       { whenOff: 'No Likes' },
  bookmarks:   { whenOn: 'Bookmarks' },
  user_tweets: { whenOn: 'User Tweets' },
  reddit:      { whenOn: 'Reddit' },
  roam:        { whenOff: 'No Roam' },
  withImages:  { whenOff: 'No Images' },
  withVideos:  { whenOff: 'No Videos' },
  textOnly:    { whenOff: 'No Text-Only' },
  favorites:   { whenOn: 'Favorites Only' },
  hideSkipped: { whenOff: 'Showing Skipped' },
};

/**
 * SearchBar component with username suggestions and filter options
 */
const SearchBar = ({
  allTweets,
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
  onSearch
}) => {
  const { isDevMode } = useDevMode();
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const searchInputRef = useRef(null);
  
  // Create debounced search function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((term) => {
      setSearchTerm(term);
      // Always call onSearch to update URL
      onSearch?.(term);
    }, 300),
    [setSearchTerm, onSearch]
  );
  
  // Setup search suggestions
  const {
    isShowingSuggestions,
    currentOperator,
    suggestionQuery,
    suggestions,
    selectSuggestion,
    hideSuggestions
  } = useSearchSuggestions(allTweets, searchTerm, cursorPosition);
  
  // Update local search term when parent searchTerm changes
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);
  
  // Clean up debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);
  
  // Handle input changes
  const handleSearchChange = (e) => {
    const newSearch = e.target.value;
    
    // Update local state immediately for responsive UI
    setLocalSearchTerm(newSearch);
    
    // Debounce the actual search execution
    debouncedSearch(newSearch);
    
    // Update cursor position for username suggestions
    if (searchInputRef.current) {
      setCursorPosition(searchInputRef.current.selectionStart || 0);
    }
  };
  
  // Track cursor position on click
  const handleInputClick = () => {
    if (searchInputRef.current) {
      setCursorPosition(searchInputRef.current.selectionStart || 0);
    }
  };
  
  // Track cursor position on keydown
  const handleInputKeyDown = (e) => {
    if (searchInputRef.current) {
      setCursorPosition(searchInputRef.current.selectionStart || 0);
    }
    
    // Hide suggestions on Escape
    if (e.key === 'Escape') {
      hideSuggestions();
    }
  };
  
  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    const result = selectSuggestion(suggestion);
    if (result) {
      const { newQuery, newPosition } = result;
      
      // Update local search term immediately
      setLocalSearchTerm(newQuery);
      
      // Update the actual search term with debouncing
      debouncedSearch(newQuery);
      
      // Focus back on input and set cursor position
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.selectionStart = newPosition;
          searchInputRef.current.selectionEnd = newPosition;
          setCursorPosition(newPosition);
        }
      }, 0);
    }
  };
  
  // Handle suggestion keydown
  const handleSuggestionKeyDown = (e) => {
    if (e.key === 'Escape') {
      hideSuggestions();
      // Focus back on input
      searchInputRef.current?.focus();
    }
  };
  
  // Handle input blur
  const handleInputBlur = () => {
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => {
      hideSuggestions();
    }, 200);
  };

  // Compute active filter chips (filters that differ from defaults)
  const activeFilterChips = Object.entries(FILTER_CHIP_LABELS).reduce((chips, [key, labels]) => {
    if (key === 'hideSkipped' && !isDevMode) return chips;
    const defaultVal = DEFAULT_FILTERS[key];
    const currentVal = filters[key];
    if (currentVal === defaultVal) return chips;
    const label = currentVal ? labels.whenOn : labels.whenOff;
    if (label) chips.push({ key, label });
    return chips;
  }, []);

  const clearChip = (key) => {
    setFilters(prev => ({ ...prev, [key]: DEFAULT_FILTERS[key] }));
  };

  // Handle clear button click
  const handleClear = () => {
    setLocalSearchTerm('');
    setSearchTerm('');
    // Call onSearch to update URL
    onSearch?.('');
    // Focus back on input
    searchInputRef.current?.focus();
  };

  // Handle quote button click
  const handleQuoteWrap = () => {
    const currentValue = localSearchTerm.trim();
    let newValue;

    if (!currentValue) {
      // If empty, just add quotes for user to type into
      newValue = '""';
    } else if (currentValue.startsWith('"') && currentValue.endsWith('"')) {
      // If already quoted, remove quotes
      newValue = currentValue.slice(1, -1);
    } else {
      // Add quotes around the current search term
      newValue = `"${currentValue}"`;
    }

    setLocalSearchTerm(newValue);
    debouncedSearch(newValue);

    // Focus back on input and position cursor
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
        // Position cursor before the closing quote if we added quotes
        if (!currentValue || !currentValue.startsWith('"')) {
          const cursorPos = newValue.length - 1;
          searchInputRef.current.selectionStart = cursorPos;
          searchInputRef.current.selectionEnd = cursorPos;
        }
      }
    }, 0);
  };

  return (
    <div className="mb-4 relative search-container">
      <SearchInputWithHighlighting
        value={localSearchTerm}
        onChange={handleSearchChange}
        onClick={handleInputClick}
        onKeyDown={handleInputKeyDown}
        onBlur={handleInputBlur}
        placeholder="Search"
        className="search-input"
        ariaLabel="Search tweets and posts"
        inputRef={searchInputRef}
      />

      <SearchSuggestions
        visible={isShowingSuggestions}
        operator={currentOperator}
        query={suggestionQuery}
        suggestions={suggestions}
        onSelect={handleSuggestionSelect}
        onKeyDown={handleSuggestionKeyDown}
      />

      <div className="search-actions-container">
        {localSearchTerm && (
          <>
            <button
              onClick={handleClear}
              className="search-action-button clear-button"
              aria-label="Clear search"
              title="Clear search"
            >
              <X size={18} />
            </button>

            <button
              onClick={handleQuoteWrap}
              className="search-action-button quote-button"
              aria-label="Wrap in quotes for exact match"
              title="Exact match (wrap in quotes)"
            >
              <Quote size={18} />
            </button>
          </>
        )}

        <button
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className={`filter-button${activeFilterChips.length > 0 ? ' filter-button--active' : ''}`}
          aria-label="Open filters"
        >
          <Filter size={20} />
          {activeFilterChips.length > 0 && (
            <span className="filter-button__badge">{activeFilterChips.length}</span>
          )}
        </button>
      </div>

      {isFilterOpen && (
        <FilterPopover
          filters={filters}
          setFilters={setFilters}
          onClose={() => setIsFilterOpen(false)}
        />
      )}

      {activeFilterChips.length > 0 && (
        <div className="filter-chips">
          {activeFilterChips.map(({ key, label }) => (
            <button key={key} className="filter-chip" onClick={() => clearChip(key)}>
              {label}
              <X size={12} className="filter-chip__x" />
            </button>
          ))}
        </div>
      )}

      <SearchTips />
    </div>
  );
};

export default SearchBar;